import React, {useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {collectWeekendDatedItems} from './weekendTimelineGeometry';
// Both from canvasTimelinePrimitives, not canvas_timeline_util or SchedulingUtils:
// those reach FeatureUtil -> util.js -> directApi and cannot be imported from a
// test, which would make this component untestable too (PIKA-780).
import {getMomentFromCanvasTimelineDate, isTaskItemType} from './canvasTimelinePrimitives';

/**
 * PIKA-780 — option 4 from the wireframes.
 *
 * When weekends are hidden, a task dated on a Saturday or Sunday either has its
 * bar trimmed back to the working days it also covers, or — if it sits entirely
 * in the weekend — has no bar at all and cannot be hovered, clicked or linked.
 * Nothing told the user. This notice does.
 *
 * It lives in ordinary DOM above the canvas rather than inside it, which is what
 * makes it work unchanged at every zoom level: the hidden weekend gap is
 * sub-pixel at week zoom and coarser, so an in-canvas marker would need a rule
 * per zoom. It renders inside CanvasTimeline, so project, program, people,
 * placeholder, capacity and share timelines all get it from one place.
 */

/** "Sat 19 Sep" — the format the acceptance criteria ask for. */
const formatWeekendDate = canvasDate => getMomentFromCanvasTimelineDate(canvasDate).format('ddd D MMM');

/**
 * The real date or range, never the drawn one.
 *
 * A single-day task reads "Sat 19 Sep". A task that starts on the weekend and
 * runs into working days reads "Sat 19 - Tue 22 Sep", so the user can see both
 * the date the Timeline is hiding and where the bar they can see begins.
 */
export const getWeekendDateLabel = entry => {
	const {startDate, endDate} = entry;

	if (startDate === endDate) {
		return formatWeekendDate(startDate);
	}

	const start = getMomentFromCanvasTimelineDate(startDate);
	const end = getMomentFromCanvasTimelineDate(endDate);
	// Moment's month granularity already compares the year, so no second check.
	const sameMonth = start.isSame(end, 'month');

	// Drop the repeated month when both ends share it: "Sat 19 - Tue 22 Sep".
	return `${sameMonth ? start.format('ddd D') : formatWeekendDate(startDate)} - ${formatWeekendDate(endDate)}`;
};

/**
 * One entry per task, not per bar.
 *
 * People Scheduling composes a separate item for every assignee of a task, so a
 * task assigned to three people arrives here three times. Counting those as
 * three tasks would overstate the notice and repeat the same row, so collapse
 * them on the task id.
 *
 * The filter mirrors `drawGroupItems` exactly: an item the page's search or
 * filters removed is dropped, because the user cannot see it at all and there is
 * nothing to explain, while an item being dragged is kept, as it is still drawn.
 *
 * Note that `item.visible` does NOT track group collapse — `hide()` sets it, and
 * only the drag handler calls that — so a task inside a collapsed project is
 * still counted and listed. That is deliberate: it is hidden by the weekend
 * setting whether or not its group happens to be open, which is precisely what
 * this notice exists to surface.
 */
/**
 * Ids of groups whose items the Timeline is not drawing.
 *
 * Filtering is applied in two different places depending on the surface. People
 * Scheduling marks the items (`item.filtered`, via filterGroupsItemsCached), but
 * the project, connected-group and Program Timelines mark the task GROUP instead
 * — `group.filtered = !matchesFilter || isFilteredBySearch(group)` in
 * ProjectsSchedulingFilterLogic — and never touch the item. Checking only the
 * item therefore left the notice counting tasks a search had removed from the
 * canvas (QA D1b), so both have to be checked.
 *
 * A filtered group hides its descendants too, hence the inherited flag. Note
 * that `filtered` is not `expanded`: collapsing a phase does not filter it, so
 * tasks inside a collapsed group stay counted, as documented above.
 */
const collectFilteredGroupIds = (groups, hasFilteredAncestor = false, filteredIds = new Set()) => {
	for (const group of groups || []) {
		const isFiltered = hasFilteredAncestor || !!group.filtered;

		if (isFiltered) {
			filteredIds.add(group.id);
		}

		if (group.groups?.length) {
			collectFilteredGroupIds(group.groups, isFiltered, filteredIds);
		}
	}

	return filteredIds;
};

const getWeekendDatedTasks = (items, groups, hideWeekends) => {
	// Nothing is reported when weekends are shown, so skip the group walk too.
	if (!hideWeekends) return [];

	const filteredGroupIds = collectFilteredGroupIds(groups);
	const drawnTaskItems = (items || []).filter(
		item =>
			isTaskItemType(item) &&
			!item.filtered &&
			!filteredGroupIds.has(item.groupId) &&
			(item.visible || item.isBeingDragged)
	);
	const entries = collectWeekendDatedItems(drawnTaskItems, hideWeekends);
	const byTaskId = new Map();

	for (const entry of entries) {
		const key = entry.item.data?.task?.id ?? entry.item.data?.task?.companyTaskId ?? entry.item;
		if (!byTaskId.has(key)) {
			byTaskId.set(key, entry);
		}
	}

	return [...byTaskId.values()];
};

const WeekendTaskNotice = ({items, groups, hideWeekends, showTaskModal, onLayoutChange}) => {
	const {formatMessage} = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	// Recomputed on every render, deliberately: the Timeline mutates its items IN
	// PLACE -- FilterUtils assigns item.filtered, and drag and resize write the new
	// dates onto the same objects -- so the `items` array reference never changes.
	// Memoising on [items, hideWeekends] therefore never invalidates, and the
	// notice kept showing the old count and dates after a drag, resize or search
	// until a full reload (QA D1). Correctness wins over the saved scan here; the
	// work is one filter and one map over items already in memory, and
	// collectWeekendDatedItems returns immediately when weekends are shown, which
	// is also the "nothing changes when weekends are visible" guarantee.
	//
	// Dropping the memo is necessary but not sufficient: those mutations happen
	// after React has rendered, in the forced-redraw that follows, so nothing would
	// re-render this component either. CanvasTimeline bumps `itemRevision` in
	// onForceRedrawEventReceived to drive that render. The two go together.
	const weekendDatedTasks = getWeekendDatedTasks(items, groups, hideWeekends);
	const hasNotice = weekendDatedTasks.length > 0;

	// Appearing or disappearing changes the canvas height, so the Timeline has to
	// re-measure; it otherwise only does so on a window resize. Expanding does NOT
	// need to be reported, because the dates list is an overlay and takes no layout
	// space. Skipped on the first commit, where componentDidMount already measures.
	const lastReportedLayout = useRef(null);
	useLayoutEffect(() => {
		const isFirstLayout = lastReportedLayout.current === null;
		const hasChanged = lastReportedLayout.current !== hasNotice;
		lastReportedLayout.current = hasNotice;

		if (!isFirstLayout && hasChanged && onLayoutChange) {
			onLayoutChange();
		}
	}, [hasNotice, onLayoutChange]);

	if (!hasNotice) return null;

	const canOpenTask = typeof showTaskModal === 'function';

	return (
		<div className="weekend-task-notice" data-cy="weekend-task-notice">
			<div className="weekend-task-notice-summary">
				<span className="weekend-task-notice-text">
					{formatMessage({id: 'scheduling.weekend_dated_tasks_notice'}, {taskCount: weekendDatedTasks.length})}
				</span>
				<button
					type="button"
					className="weekend-task-notice-toggle"
					data-cy="weekend-task-notice-toggle"
					aria-expanded={isExpanded}
					onClick={() => setIsExpanded(expanded => !expanded)}
				>
					{formatMessage({
						id: isExpanded
							? 'scheduling.weekend_dated_tasks_hide_dates'
							: 'scheduling.weekend_dated_tasks_view_dates',
					})}
				</button>
			</div>
			{isExpanded && (
				<ul className="weekend-task-notice-list">
					{weekendDatedTasks.map(entry => {
						const task = entry.item.data?.task;
						const name = task?.name || '';
						const dateLabel = getWeekendDateLabel(entry);

						return (
							<li key={task?.id ?? `${task?.companyTaskId}-${entry.startDate}-${entry.endDate}`}>
								{canOpenTask && task?.companyTaskId ? (
									<button
										type="button"
										className="weekend-task-notice-task"
										onClick={() => showTaskModal(task.companyTaskId)}
									>
										{name}
									</button>
								) : (
									<span className="weekend-task-notice-task-name">{name}</span>
								)}
								<span className="weekend-task-notice-date">{dateLabel}</span>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

WeekendTaskNotice.propTypes = {
	items: PropTypes.array,
	/** Needed because the project and Program Timelines filter groups, not items. */
	groups: PropTypes.array,
	hideWeekends: PropTypes.bool,
	/**
	 * Withheld on the share view — it has no task modal to open, so the list
	 * degrades to plain text rather than offering buttons that do nothing.
	 */
	showTaskModal: PropTypes.func,
	/**
	 * Called when the notice appears or disappears, so the Timeline can re-measure
	 * the canvas. Not called on expand — the dates list overlays rather than
	 * displaces the canvas, so the layout is unchanged.
	 */
	onLayoutChange: PropTypes.func,
};

export default WeekendTaskNotice;
